import { Controller } from "@hotwired/stimulus";

export class ValidationCodeController extends Controller {
  static targets = ["digit", "code", "submit"];
  static values = { length: Number };

  connect() {
    // Ensure a consistent length (in case markup changes)
    if (this.lengthValue && this.digitTargets.length !== this.lengthValue) {
      // not fatal; proceed with actual inputs present
    }

    this.digitTargets.forEach((el) => (el.value = ""));
    this.update();
    this.digitTargets[0]?.focus();
  }

  sanitizeChar(value) {
    // Keep only 0-9 A-Z, take first char, uppercase
    return (value || "")
      .toString()
      .toUpperCase()
      .replace(/[^0-9A-Z]/g, "")
      .slice(0, 1);
  }

  sanitizeString(value) {
    return (value || "")
      .toString()
      .toUpperCase()
      .replace(/[^0-9A-Z]/g, "");
  }

  onInput(event) {
    const el = event.target;
    el.value = this.sanitizeChar(el.value);

    const idx = this.digitTargets.indexOf(el);
    if (el.value && idx < this.digitTargets.length - 1) {
      this.digitTargets[idx + 1].focus();
      this.digitTargets[idx + 1].select?.();
    }

    this.update();
  }

  onKeydown(event) {
    const el = event.target;
    const idx = this.digitTargets.indexOf(el);

    if (event.key === "Backspace") {
      event.preventDefault();

      if (el.value) {
        // Clear current if it has a value
        el.value = "";
        this.update();
        return;
      }

      // If current is empty, move left and clear that
      if (idx > 0) {
        const prev = this.digitTargets[idx - 1];
        prev.focus();
        prev.value = "";
        this.update();
      }
      return;
    }

    if (event.key === "Delete") {
      event.preventDefault();
      el.value = "";
      this.update();
      return;
    }

    if (event.key === "ArrowLeft") {
      if (idx > 0) {
        event.preventDefault();
        this.digitTargets[idx - 1].focus();
      }
      return;
    }

    if (event.key === "ArrowRight") {
      if (idx < this.digitTargets.length - 1) {
        event.preventDefault();
        this.digitTargets[idx + 1].focus();
      }
      return;
    }

    // Allow navigation/control keys
    if (event.key.length !== 1) return;

    // Block non-alphanumeric keystrokes at keydown for better UX
    const ch = event.key.toUpperCase();
    if (!/[0-9A-Z]/.test(ch) && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
    }
  }

  onPaste(event) {
    const text = (event.clipboardData || window.clipboardData).getData("text");
    const clean = this.sanitizeString(text).slice(0, this.digitTargets.length);
    if (!clean) return;

    event.preventDefault();

    for (let i = 0; i < this.digitTargets.length; i++) {
      this.digitTargets[i].value = clean[i] || "";
    }

    const nextEmpty = this.digitTargets.find((d) => !d.value);
    (nextEmpty || this.digitTargets[this.digitTargets.length - 1])?.focus();

    this.update();
  }

  update() {
    const joined = this.digitTargets
      .map((d) => this.sanitizeChar(d.value))
      .join("");
    this.codeTarget.value = joined;

    const complete = joined.length === this.digitTargets.length;
    this.submitTarget.disabled = !complete;
  }
}
